import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { 
  Music, 
  Palette, 
  Users, 
  Settings, 
  Plus,
  Edit,
  Trash2,
  Upload,
  Save,
  Download,
  LogOut,
  Home
} from 'lucide-react';
import BunnyImportNew from './BunnyImportNew';
import EditSongModal from './EditSongModal';
import { apiCache } from '../utils/apiCache';

const AdminPanel = ({ songs, vibes, isAdmin, refreshSongs: refreshGlobalSongs }) => {
  const location = useLocation();
  const navigate = useNavigate();

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleLogout = () => {
    localStorage.clear(); // Clear all localStorage
    document.cookie = 'session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    window.location.href = '/login'; // Use href instead of navigate for full reload
  };

  const adminNavItems = [
    { icon: Music, label: 'Songs', path: '/admin' },
    { icon: Palette, label: 'Vibes', path: '/admin/vibes' },
    { icon: Music, label: 'Genres', path: '/admin/genres' },
    { icon: Settings, label: 'Tags', path: '/admin/tags' },
    { icon: Users, label: 'Users', path: '/admin/users' },
    { icon: Upload, label: 'Ads', path: '/admin/ads' },
    { icon: Settings, label: 'Settings', path: '/admin/settings' }
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/admin/';
    }
    return location.pathname === path;
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div>
          <h1>Admin Panel</h1>
          <p>Manage your MetaSoul® Music platform</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={() => navigate('/')}
            className="admin-button secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Home size={18} />
            Home
          </button>
          <button 
            onClick={handleLogout}
            className="admin-button secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      <div className="admin-nav">
        {adminNavItems.map(({ icon: Icon, label, path }) => (
          <Link
            key={path}
            to={path}
            className={`admin-nav-item ${isActive(path) ? 'active' : ''}`}
          >
            <Icon size={20} />
            <span>{label}</span>
          </Link>
        ))}
      </div>

      <div className="admin-content">
        <Routes>
          <Route path="/" element={<SongsManager songs={songs} vibes={vibes} refreshGlobalSongs={refreshGlobalSongs} />} />
          <Route path="/vibes" element={<VibesManager vibes={vibes} songs={songs} />} />
          <Route path="/genres" element={<GenresManager songs={songs} />} />
          <Route path="/tags" element={<TagsManager />} />
          <Route path="/users" element={<UsersManager />} />
          <Route path="/ads" element={<AdsManager />} />
          <Route path="/settings" element={<SettingsManager />} />
        </Routes>
      </div>
    </div>
  );
};

const SongsManager = ({ songs: initialSongs, vibes, refreshGlobalSongs }) => {
  const [songs, setSongs] = useState(initialSongs || []);
  const [selectedSongs, setSelectedSongs] = useState(new Set());
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showBunnyImport, setShowBunnyImport] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('title'); // title, artist, album, created_at
  const [sortOrder, setSortOrder] = useState('asc'); // asc, desc

  // Update songs when initialSongs prop changes
  useEffect(() => {
    if (initialSongs) {
      setSongs(initialSongs);
      console.log('🔄 SongsManager: Updated songs from props', initialSongs.length);
    }
  }, [initialSongs]);
  
  // Sort songs based on current sort settings
  const sortedSongs = React.useMemo(() => {
    const sorted = [...songs].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'title':
          aVal = a.title.toLowerCase();
          bVal = b.title.toLowerCase();
          break;
        case 'artist':
          aVal = a.artist.toLowerCase();
          bVal = b.artist.toLowerCase();
          break;
        case 'album':
          aVal = a.album.toLowerCase();
          bVal = b.album.toLowerCase();
          break;
        case 'created_at':
          aVal = new Date(a.created_at || 0);
          bVal = new Date(b.created_at || 0);
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [songs, sortBy, sortOrder]);
  
  // Song editing state
  const [editingSong, setEditingSong] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const toggleSongSelection = (songId) => {
    const newSelection = new Set(selectedSongs);
    if (newSelection.has(songId)) {
      newSelection.delete(songId);
    } else {
      newSelection.add(songId);
    }
    setSelectedSongs(newSelection);
  };

  const refreshSongs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/songs`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSongs(data);
        
        // Also refresh global App.js state
        if (refreshGlobalSongs) {
          await refreshGlobalSongs();
        }
      }
    } catch (error) {
      console.error('Failed to refresh songs:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteSelectedSongs = async () => {
    if (selectedSongs.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedSongs.size} songs?`)) {
      return;
    }

    setLoading(true);
    try {
      const deletePromises = Array.from(selectedSongs).map(songId =>
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/songs/${songId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        })
      );

      await Promise.all(deletePromises);
      setSelectedSongs(new Set());
      await refreshSongs();
    } catch (error) {
      console.error('Failed to delete songs:', error);
      alert('Failed to delete some songs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshSongs();
  }, []);

  return (
    <div className="admin-section">
      <div className="section-header">
        <div>
          <h2>Songs Management</h2>
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '14px', color: '#666' }}>Sort by:</span>
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '14px',
                cursor: 'pointer',
                color: '#333',
                backgroundColor: 'white'
              }}
            >
              <option value="title">Title</option>
              <option value="artist">Artist</option>
              <option value="album">Album</option>
              <option value="created_at">Date Added</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                background: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#333'
              }}
            >
              {sortOrder === 'asc' ? '↑ A-Z' : '↓ Z-A'}
            </button>
          </div>
        </div>
        <div className="section-actions">
          <button 
            className="admin-button secondary"
            onClick={() => setShowBunnyImport(!showBunnyImport)}
          >
            <Download size={16} />
            Import from Bunny
          </button>
          <button 
            className="admin-button primary"
            onClick={() => setShowUploadForm(true)}
          >
            <Upload size={16} />
            Upload Song
          </button>
          {selectedSongs.size > 0 && (
            <button 
              className="admin-button danger"
              onClick={deleteSelectedSongs}
              disabled={loading}
            >
              <Trash2 size={16} />
              Delete Selected ({selectedSongs.size})
            </button>
          )}
        </div>
      </div>

      {showBunnyImport && (
        <BunnyImportNew 
          onImportComplete={() => {
            refreshSongs();
            setShowBunnyImport(false);
          }}
        />
      )}

      {/* Edit Song Modal */}
      {showEditModal && editingSong && (
        <EditSongModal 
          song={editingSong}
          vibes={vibes}
          onClose={() => {
            setShowEditModal(false);
            setEditingSong(null);
          }}
          onSave={async (updatedSong) => {
            console.log('📥 Received updated song from modal:', updatedSong.title);
            
            // Invalidate cache before refreshing
            apiCache.invalidate('songs');
            
            // Refresh songs list from backend to get latest data
            console.log('🔄 Refreshing songs list...');
            await refreshSongs();
            
            // Update local state immediately with the updated song
            setSongs(prevSongs => 
              prevSongs.map(s => 
                s.id === updatedSong.id ? updatedSong : s
              )
            );
            
            console.log('✅ Songs list refreshed and UI updated');
            
            // Modal will close itself after showing success message
          }}
        />
      )}

      {showUploadForm && (
        <UploadSongForm 
          vibes={vibes} 
          onClose={() => {
            setShowUploadForm(false);
            refreshSongs();
          }} 
        />
      )}

      <div className="songs-table">
        <div className="table-header">
          <div className="table-checkbox">
            <input
              type="checkbox"
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedSongs(new Set(songs.map(s => s.id)));
                } else {
                  setSelectedSongs(new Set());
                }
              }}
            />
          </div>
          <div className="table-col">Title</div>
          <div className="table-col">Artist</div>
          <div className="table-col">Album</div>
          <div className="table-col">Vibes</div>
          <div className="table-col">Duration</div>
          <div className="table-col">Actions</div>
        </div>

        {sortedSongs.map(song => (
          <div key={song.id} className="table-row">
            <div className="table-checkbox">
              <input
                type="checkbox"
                checked={selectedSongs.has(song.id)}
                onChange={() => toggleSongSelection(song.id)}
              />
            </div>
            <div className="table-col">
              <div className="song-info">
                <img src={song.albumArt} alt={song.title} className="song-thumb" />
                <span>{song.title}</span>
              </div>
            </div>
            <div className="table-col">{song.artist}</div>
            <div className="table-col">{song.album}</div>
            <div className="table-col">
              <div className="vibes-tags">
                {song.vibes.map(vibeId => (
                  <span key={vibeId} className="vibe-tag small">
                    {vibes.find(v => v.id === vibeId)?.name}
                  </span>
                ))}
              </div>
            </div>
            <div className="table-col">
              {Math.floor(song.duration / 60)}:{(song.duration % 60).toString().padStart(2, '0')}
            </div>
            <div className="table-col">
              <div className="table-actions">
                <button 
                  className="action-button"
                  onClick={() => {
                    console.log('🎵 SongsManager: Opening edit modal for song:', song.title);
                    console.log('🏷️ SongsManager: Song tags at click time:', JSON.stringify(song.tags));
                    setEditingSong(song);
                    setShowEditModal(true);
                  }}
                >
                  <Edit size={16} />
                </button>
                <button 
                  className="action-button danger"
                  onClick={async () => {
                    if (!window.confirm(`Are you sure you want to delete "${song.title}"?`)) {
                      return;
                    }
                    
                    setLoading(true);
                    try {
                      const response = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/songs/${song.id}`,
                        {
                          method: 'DELETE',
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                          }
                        }
                      );
                      
                      if (response.ok) {
                        console.log('✅ Song deleted:', song.title);
                        await refreshSongs();
                      } else {
                        alert('Failed to delete song. Please try again.');
                      }
                    } catch (error) {
                      console.error('Failed to delete song:', error);
                      alert('Failed to delete song. Please try again.');
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const VibesManager = ({ vibes, songs }) => {
  const navigate = useNavigate();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingVibe, setEditingVibe] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [persistedError, setPersistedError] = useState('');
  const [showAllSongsModal, setShowAllSongsModal] = useState(false);
  const [selectedVibeForSongs, setSelectedVibeForSongs] = useState(null);
  const [removingSongId, setRemovingSongId] = useState(null);
  const [selectedSongs, setSelectedSongs] = useState(new Set());
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRefresh = () => {
    // Invalidate cache to ensure fresh data
    apiCache.invalidate('vibes');
    apiCache.invalidate('songs');
    
    // Navigate to force remount and refresh data
    navigate('/admin/vibes', { replace: true });
    window.location.reload();
  };

  const handleRemoveSongFromVibe = async (songId, vibeId) => {
    if (!window.confirm('Are you sure you want to remove this song from this vibe?')) {
      return;
    }

    setRemovingSongId(songId);

    try {
      // Find the song
      const song = songs.find(s => s.id === songId);
      if (!song) {
        alert('Song not found');
        setRemovingSongId(null);
        return;
      }

      // Remove the vibe from the song's vibes array
      const updatedVibes = song.vibes.filter(v => v !== vibeId);

      console.log(`🔄 Removing vibe ${vibeId} from song ${songId}`);
      console.log(`📝 Current vibes:`, song.vibes);
      console.log(`📝 Updated vibes:`, updatedVibes);

      // Update the song
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/songs/${songId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            vibes: updatedVibes
          })
        }
      );

      if (response.ok) {
        console.log('✅ Song removed from vibe successfully');
        
        // Invalidate cache immediately
        apiCache.invalidate('vibes');
        apiCache.invalidate('songs');
        
        // Small delay to ensure backend has processed the update
        await new Promise(resolve => setTimeout(resolve, 300));
        handleRefresh();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to remove song from vibe');
        setRemovingSongId(null);
      }
    } catch (error) {
      console.error('Error removing song from vibe:', error);
      alert('Failed to remove song from vibe');
      setRemovingSongId(null);
    }
  };

  const handleToggleSongSelect = (songId) => {
    const newSelected = new Set(selectedSongs);
    if (newSelected.has(songId)) {
      newSelected.delete(songId);
    } else {
      newSelected.add(songId);
    }
    setSelectedSongs(newSelected);
  };

  const handleRemoveSelectedSongs = async (vibeId) => {
    if (selectedSongs.size === 0) {
      alert('Please select songs to remove');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${selectedSongs.size} song(s) from this vibe?`)) {
      return;
    }

    setIsRemoving(true);

    try {
      const promises = Array.from(selectedSongs).map(async (songId) => {
        const song = songs.find(s => s.id === songId);
        if (!song) return null;

        const updatedVibes = song.vibes.filter(v => v !== vibeId);

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/songs/${songId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
            body: JSON.stringify({
              vibes: updatedVibes
            })
          }
        );

        return response.ok;
      });

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r === true).length;

      console.log(`✅ Removed ${successCount} songs from vibe`);
      
      // Clear selection
      setSelectedSongs(new Set());
      
      // Small delay to ensure backend has processed updates
      await new Promise(resolve => setTimeout(resolve, 300));
      handleRefresh();
    } catch (error) {
      console.error('Error removing songs from vibe:', error);
      alert('Failed to remove some songs from vibe');
      setIsRemoving(false);
    }
  };

  const handleShowAllSongs = (vibe) => {
    setSelectedVibeForSongs(vibe);
    setSelectedSongs(new Set()); // Clear selection when opening modal
    setShowAllSongsModal(true);
  };

  // Check for persisted errors on mount
  React.useEffect(() => {
    const errorData = localStorage.getItem('vibe_upload_error');
    if (errorData) {
      try {
        const { error, timestamp } = JSON.parse(errorData);
        // Only show error if it's less than 30 seconds old
        if (Date.now() - timestamp < 30000) {
          setPersistedError(error);
          console.error('❌ Previous upload error:', error);
        }
        // Clear the error from storage
        localStorage.removeItem('vibe_upload_error');
      } catch (e) {
        console.error('Error parsing stored error:', e);
      }
    }
  }, []);

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Vibes Management</h2>
        <button 
          className="admin-button primary"
          onClick={() => setShowCreateForm(true)}
        >
          <Plus size={16} />
          Create Vibe
        </button>
      </div>

      {persistedError && (
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '16px',
          color: '#c00'
        }}>
          <strong>Previous Upload Error:</strong> {persistedError}
          <button 
            onClick={() => setPersistedError('')}
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      )}

      {showCreateForm && (
        <CreateVibeForm 
          onClose={() => setShowCreateForm(false)} 
          onSuccess={handleRefresh}
        />
      )}
      
      {showEditModal && editingVibe && (
        <EditVibeModal 
          vibe={editingVibe}
          onClose={() => {
            setShowEditModal(false);
            setEditingVibe(null);
          }}
          onSave={() => {
            setShowEditModal(false);
            setEditingVibe(null);
            handleRefresh(); // Refresh vibes list
          }}
        />
      )}

      <div className="vibes-grid">
        {vibes.map(vibe => {
          const vibeSongs = songs.filter(song => song.vibes.includes(vibe.id));
          
          return (
            <div key={vibe.id} className="vibe-admin-card">
              <div 
                className="vibe-admin-header"
                style={{ backgroundColor: vibe.color }}
              >
                <h3>{vibe.name}</h3>
                <div className="vibe-admin-actions">
                  <button 
                    className="action-button"
                    onClick={() => {
                      setEditingVibe(vibe);
                      setShowEditModal(true);
                    }}
                  >
                    <Edit size={16} />
                  </button>
                  <button className="action-button danger">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              
              <div className="vibe-admin-content">
                <p className="vibe-description">{vibe.description}</p>
                <div className="vibe-stats">
                  <span>{vibeSongs.length} songs</span>
                </div>
                
                <div className="vibe-songs-list">
                  <h4>Songs in this vibe:</h4>
                  {vibeSongs.slice(0, 3).map(song => (
                    <div key={song.id} className="vibe-song-item">
                      <span>{song.title}</span>
                      <button 
                        className="remove-song-btn"
                        onClick={() => handleRemoveSongFromVibe(song.id, vibe.id)}
                        disabled={removingSongId === song.id}
                        style={{ cursor: removingSongId === song.id ? 'wait' : 'pointer' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {vibeSongs.length > 3 && (
                    <div 
                      className="vibe-song-item more-songs"
                      onClick={() => handleShowAllSongs(vibe)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span style={{ color: '#9D4EDD', fontWeight: '600' }}>
                        +{vibeSongs.length - 3} more songs
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* All Songs Modal */}
      {showAllSongsModal && selectedVibeForSongs && (
        <div className="modal-overlay" onClick={() => setShowAllSongsModal(false)}>
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '700px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid #404040'
            }}>
              <h2 style={{ margin: 0, color: '#fff' }}>
                All Songs in {selectedVibeForSongs.name}
              </h2>
              <button
                onClick={() => setShowAllSongsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#b3b3b3',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '32px',
                  height: '32px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px' 
            }}>
              <div style={{ color: '#b3b3b3' }}>
                {songs.filter(song => song.vibes.includes(selectedVibeForSongs.id)).length} songs total
                {selectedSongs.size > 0 && (
                  <span style={{ color: '#9D4EDD', marginLeft: '12px' }}>
                    • {selectedSongs.size} selected
                  </span>
                )}
              </div>
              {selectedSongs.size > 0 && (
                <button
                  onClick={() => handleRemoveSelectedSongs(selectedVibeForSongs.id)}
                  disabled={isRemoving}
                  style={{
                    padding: '8px 16px',
                    background: 'rgba(255, 71, 87, 0.1)',
                    border: '1px solid rgba(255, 71, 87, 0.3)',
                    borderRadius: '6px',
                    color: '#ff4757',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isRemoving ? 'wait' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {isRemoving ? 'Removing...' : `Remove ${selectedSongs.size} Song(s)`}
                </button>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', flex: 1 }}>
              {songs
                .filter(song => song.vibes.includes(selectedVibeForSongs.id))
                .map(song => (
                  <div 
                    key={song.id} 
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: selectedSongs.has(song.id) ? 'rgba(157, 78, 221, 0.1)' : '#282828',
                      borderRadius: '8px',
                      border: selectedSongs.has(song.id) ? '1px solid #9D4EDD' : '1px solid #404040',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={selectedSongs.has(song.id)}
                        onChange={() => handleToggleSongSelect(song.id)}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                          accentColor: '#9D4EDD'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#fff', fontWeight: '500', marginBottom: '4px' }}>
                          {song.title}
                        </div>
                        <div style={{ color: '#b3b3b3', fontSize: '14px' }}>
                          {song.artist}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveSongFromVibe(song.id, selectedVibeForSongs.id)}
                      disabled={removingSongId === song.id}
                      style={{
                        background: 'rgba(255, 71, 87, 0.1)',
                        border: '1px solid rgba(255, 71, 87, 0.3)',
                        color: '#ff4757',
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                        cursor: removingSongId === song.id ? 'wait' : 'pointer',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        flexShrink: 0
                      }}
                      onMouseOver={(e) => {
                        if (removingSongId !== song.id) {
                          e.currentTarget.style.background = 'rgba(255, 71, 87, 0.2)';
                        }
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 71, 87, 0.1)';
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



const TagsManager = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTag, setNewTag] = useState({ name: '', category: 'mood' });

  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tags`
      );
      if (response.ok) {
        const data = await response.json();
        setTags(data);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const handleCreateTag = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tags`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify(newTag)
        }
      );
      
      if (response.ok) {
        setNewTag({ name: '', category: 'mood' });
        setShowCreateForm(false);
        fetchTags();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create tag');
      }
    } catch (error) {
      console.error('Error creating tag:', error);
      alert('Failed to create tag');
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm(`Delete tag "${tagId}"? This will remove it from all songs.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tags/${tagId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );
      
      if (response.ok) {
        fetchTags();
      } else {
        alert('Failed to delete tag');
      }
    } catch (error) {
      console.error('Error deleting tag:', error);
      alert('Failed to delete tag');
    }
  };

  const tagsByCategory = tags.reduce((acc, tag) => {
    const category = tag.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(tag);
    return acc;
  }, {});

  return (
    <div className="tags-manager">
      <div className="admin-header">
        <h2>Manage Tags</h2>
        <button className="admin-button primary" onClick={() => setShowCreateForm(true)}>
          Create Tag
        </button>
      </div>

      {showCreateForm && (
        <div className="modal-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create New Tag</h2>
            <form onSubmit={handleCreateTag}>
              <div className="form-group">
                <label>Tag Name</label>
                <input
                  type="text"
                  value={newTag.name}
                  onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                  placeholder="e.g., energizing, peaceful"
                  required
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select
                  value={newTag.category}
                  onChange={(e) => setNewTag({ ...newTag, category: e.target.value })}
                >
                  <option value="mood">Mood</option>
                  <option value="activity">Activity</option>
                  <option value="vibe">Vibe</option>
                  <option value="sound">Sound</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="button" className="admin-button secondary" onClick={() => setShowCreateForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="admin-button primary">
                  Create Tag
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading tags...</p>
      ) : (
        <div className="tags-content">
          {Object.keys(tagsByCategory).length === 0 ? (
            <p>No tags yet. Create your first tag!</p>
          ) : (
            Object.entries(tagsByCategory).map(([category, categoryTags]) => (
              <div key={category} className="tag-category-section" style={{ marginBottom: '24px' }}>
                <h3 style={{ 
                  textTransform: 'capitalize', 
                  color: '#9D4EDD',
                  marginBottom: '12px',
                  fontSize: '18px'
                }}>
                  {category} Tags ({categoryTags.length})
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '12px'
                }}>
                  {categoryTags.map(tag => (
                    <div 
                      key={tag.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        background: 'white',
                        border: '2px solid #9D4EDD',
                        borderRadius: '8px'
                      }}
                    >
                      <span style={{ fontWeight: '500', color: '#333' }}>{tag.name}</span>
                      <button
                        onClick={() => handleDeleteTag(tag.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#e63946',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const GenresManager = ({ songs }) => {
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGenre, setEditingGenre] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchGenres();
  }, []);

  const fetchGenres = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/genres`);
      if (response.ok) {
        const data = await response.json();
        setGenres(data);
      }
    } catch (error) {
      console.error('Failed to fetch genres:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (genreId) => {
    if (!window.confirm('Are you sure you want to delete this genre?')) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/genres/${genreId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      if (response.ok) fetchGenres();
    } catch (error) {
      console.error('Failed to delete genre:', error);
    }
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Genres Management</h2>
        <button 
          className="admin-button primary"
          onClick={() => setShowCreateForm(true)}
        >
          <Plus size={16} />
          Create Genre
        </button>
      </div>

      {showCreateForm && (
        <CreateGenreForm 
          onClose={() => {
            setShowCreateForm(false);
            fetchGenres();
          }} 
        />
      )}
      
      {showEditModal && editingGenre && (
        <EditGenreModal 
          genre={editingGenre}
          onClose={() => {
            setShowEditModal(false);
            setEditingGenre(null);
          }}
          onSave={() => {
            setShowEditModal(false);
            setEditingGenre(null);
            fetchGenres();
          }}
        />
      )}

      <div className="users-table">
        <div className="table-header">
          <div className="table-col">Name</div>
          <div className="table-col">Slug</div>
          <div className="table-col">Description</div>
          <div className="table-col">Songs</div>
          <div className="table-col">Actions</div>
        </div>

        {loading ? (
          <div className="table-row">
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
              Loading genres...
            </div>
          </div>
        ) : genres.length === 0 ? (
          <div className="table-row">
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
              No genres found. Create your first genre!
            </div>
          </div>
        ) : (
          genres.map(genre => {
            const genreSongs = songs ? songs.filter(s => s.genre === genre.slug) : [];
            return (
              <div key={genre.id} className="table-row">
                <div className="table-col">{genre.name}</div>
                <div className="table-col">{genre.slug}</div>
                <div className="table-col">{genre.description || '-'}</div>
                <div className="table-col">{genreSongs.length}</div>
                <div className="table-col">
                  <button 
                    className="action-button"
                    onClick={() => {
                      setEditingGenre(genre);
                      setShowEditModal(true);
                    }}
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    className="action-button danger"
                    onClick={() => handleDelete(genre.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const UsersManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', is_admin: false });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching users from API...');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/all`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      console.log('📡 Users API response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Users fetched:', data.length, 'users');
        console.log('👥 Users data:', data);
        setUsers(data);
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch users:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user) => {
    console.log('🔵 Edit button clicked for user:', user);
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      is_admin: user.is_admin || false
    });
    setNewPassword('');
    setShowPasswordReset(false);
    setShowPassword(false);
    setShowEditModal(true);
    setMessage({ type: '', text: '' });
    console.log('🔵 Modal should now be visible. showEditModal:', true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    if (!window.confirm(`Are you sure you want to reset the password for ${editingUser.name}?`)) {
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${editingUser.id}/reset-password?new_password=${encodeURIComponent(newPassword)}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password reset successfully!' });
        setNewPassword('');
        setShowPasswordReset(false);
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to reset password' });
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setMessage({ type: 'error', text: 'Failed to reset password' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      setMessage({ type: 'error', text: 'Name and email are required' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${editingUser.id}/edit`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify(formData)
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'User updated successfully!' });
        await fetchUsers();
        setTimeout(() => {
          setShowEditModal(false);
          setEditingUser(null);
        }, 1000);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to update user' });
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setMessage({ type: 'error', text: 'Failed to update user' });
    } finally {
      setSaving(false);
    }
  };

  const handleBlockUser = async (user) => {
    if (!window.confirm(`Are you sure you want to block ${user.name}? They will not be able to login.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}/block`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        alert('User blocked successfully');
        await fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to block user');
      }
    } catch (error) {
      console.error('Error blocking user:', error);
      alert('Failed to block user');
    }
  };

  const handleActivateUser = async (user) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}/activate`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        alert('User activated successfully');
        await fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to activate user');
      }
    } catch (error) {
      console.error('Error activating user:', error);
      alert('Failed to activate user');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`⚠️ PERMANENT DELETE\n\nAre you sure you want to permanently delete ${user.name}?\n\nThis action CANNOT be undone. All user data will be permanently removed.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        alert('User deleted successfully');
        await fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Users Management</h2>
      </div>

      <div className="users-table">
        <div className="table-header">
          <div className="table-col">Name</div>
          <div className="table-col">Email</div>
          <div className="table-col">Role</div>
          <div className="table-col">Status</div>
          <div className="table-col">Join Date</div>
          <div className="table-col">Actions</div>
        </div>

        {loading ? (
          <div className="table-row">
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
              Loading users...
            </div>
          </div>
        ) : users.length === 0 ? (
          <div className="table-row">
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
              No users found
            </div>
          </div>
        ) : (
          users.map(user => (
            <div key={user.id} className="table-row">
              <div className="table-col">{user.name}</div>
              <div className="table-col">{user.email}</div>
              <div className="table-col">
                <span className={`role-badge ${user.is_admin ? 'admin' : 'user'}`}>
                  {user.is_admin ? 'Admin' : 'User'}
                </span>
              </div>
              <div className="table-col">
                <span className={`status-badge ${user.status === 'blocked' ? 'blocked' : 'active'}`}>
                  {user.status === 'blocked' ? 'Blocked' : 'Active'}
                </span>
              </div>
              <div className="table-col">
                {new Date(user.created_at).toLocaleDateString()}
              </div>
              <div className="table-col">
                <div className="table-actions">
                  <button 
                    className="action-button" 
                    onClick={() => handleEditClick(user)}
                    title="Edit user"
                  >
                    <Edit size={16} />
                  </button>
                  
                  {user.status === 'blocked' ? (
                    <button 
                      className="action-button success"
                      onClick={() => handleActivateUser(user)}
                      title="Activate user"
                      disabled={user.is_admin}
                    >
                      ✓
                    </button>
                  ) : (
                    <button 
                      className="action-button warning"
                      onClick={() => handleBlockUser(user)}
                      title="Block user"
                      disabled={user.is_admin}
                    >
                      🚫
                    </button>
                  )}
                  
                  <button 
                    className="action-button danger" 
                    onClick={() => handleDeleteUser(user)}
                    title="Delete user"
                    disabled={user.is_admin}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content user-edit-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ 
              marginBottom: '24px', 
              color: '#fff',
              fontSize: '24px',
              fontWeight: '600'
            }}>Edit User</h2>
            
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px',
                color: '#b3b3b3',
                fontSize: '14px',
                fontWeight: '500'
              }}>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="User name"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: '#282828',
                  border: '1px solid #404040',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px',
                color: '#b3b3b3',
                fontSize: '14px',
                fontWeight: '500'
              }}>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="user@example.com"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: '#282828',
                  border: '1px solid #404040',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                cursor: 'pointer',
                padding: '12px',
                background: '#282828',
                borderRadius: '8px',
                border: '1px solid #404040'
              }}>
                <input
                  type="checkbox"
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <span style={{ color: '#fff', fontSize: '14px', fontWeight: '500' }}>Admin User</span>
              </label>
            </div>

            {/* Password Reset Section */}
            <div style={{ 
              marginBottom: '24px',
              paddingTop: '24px',
              borderTop: '1px solid #404040'
            }}>
              <button
                type="button"
                onClick={() => setShowPasswordReset(!showPasswordReset)}
                style={{
                  background: 'rgba(157, 78, 221, 0.1)',
                  border: '1px solid rgba(157, 78, 221, 0.3)',
                  color: '#9D4EDD',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  width: '100%',
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>🔑 Reset Password</span>
                <span>{showPasswordReset ? '▼' : '▶'}</span>
              </button>

              {showPasswordReset && (
                <div style={{ marginTop: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px',
                    color: '#b3b3b3',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>New Password (min 6 characters)</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      style={{
                        width: '100%',
                        padding: '12px 40px 12px 16px',
                        background: '#282828',
                        border: '1px solid #404040',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '14px',
                        outline: 'none',
                        marginBottom: '12px'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: '#9D4EDD',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '12px'
                      }}
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <button
                    onClick={handleResetPassword}
                    disabled={saving || !newPassword}
                    style={{
                      padding: '10px 20px',
                      background: newPassword ? 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)' : '#404040',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: newPassword && !saving ? 'pointer' : 'not-allowed',
                      opacity: newPassword && !saving ? 1 : 0.5,
                      transition: 'all 0.2s'
                    }}
                  >
                    {saving ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              )}
            </div>

            {message.text && (
              <div style={{
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px',
                background: message.type === 'success' ? 'rgba(38, 222, 129, 0.1)' : 'rgba(255, 71, 87, 0.1)',
                color: message.type === 'success' ? '#26de81' : '#ff4757',
                border: `1px solid ${message.type === 'success' ? 'rgba(38, 222, 129, 0.3)' : 'rgba(255, 71, 87, 0.3)'}`,
                fontSize: '14px'
              }}>
                {message.text}
              </div>
            )}

            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              marginTop: '32px'
            }}>
              <button 
                onClick={() => setShowEditModal(false)}
                disabled={saving}
                style={{
                  padding: '12px 24px',
                  background: '#282828',
                  border: '1px solid #404040',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  if (!saving) {
                    e.currentTarget.style.background = '#404040';
                    e.currentTarget.style.borderColor = '#606060';
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = '#282828';
                  e.currentTarget.style.borderColor = '#404040';
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveEdit}
                disabled={saving}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(157, 78, 221, 0.3)'
                }}
                onMouseOver={(e) => {
                  if (!saving) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(157, 78, 221, 0.4)';
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(157, 78, 221, 0.3)';
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SettingsManager = () => {
  const [settings, setSettings] = useState({
    requireLoginForFavorites: true,
    requireLoginForRadio: true,
    requireLoginForPlaylists: false,
    allowGuestPlayback: true,
    maxSongsPerPlaylist: 100,
    enableSocialFeatures: false
  });

  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [bunnyStatus, setBunnyStatus] = useState('Not Configured');

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const savePlatformSettings = async () => {
    setSaving(true);
    setSaveMessage('');
    
    try {
      // Simulate API call for now - replace with real API when backend is connected
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSaveMessage('Settings saved successfully!');
      
      setTimeout(() => {
        setSaveMessage('');
      }, 3000);
    } catch (error) {
      setSaveMessage('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const checkBunnyStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/status`);
      const data = await response.json();
      setBunnyStatus(data.bunny_initialized ? 'Connected' : 'Not Configured');
    } catch (error) {
      console.error('Failed to check Bunny status:', error);
    }
  };

  useEffect(() => {
    checkBunnyStatus();
  }, []);

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Platform Settings</h2>
        <button 
          className="admin-button primary"
          onClick={savePlatformSettings}
          disabled={saving}
        >
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {saveMessage && (
        <div className={`save-message ${saveMessage.includes('success') ? 'success' : 'error'}`}>
          {saveMessage}
        </div>
      )}

      <div className="settings-form">
        {/* Bunny.net Status */}
        <div className="settings-group bunny-config">
          <h3>Bunny.net Storage Status</h3>
          <div className="bunny-status">
            <span>Status: </span>
            <span className={`status-badge ${bunnyStatus === 'Connected' ? 'connected' : 'disconnected'}`}>
              {bunnyStatus}
            </span>
          </div>
          <div className="form-help">
            ✅ Bunny.net credentials are configured on the backend. Use the "Import from Bunny" feature in Songs Management to import your audio files.
          </div>
        </div>

        <div className="settings-group">
          <h3>Authentication Settings</h3>
          
          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={settings.requireLoginForFavorites}
                onChange={(e) => handleSettingChange('requireLoginForFavorites', e.target.checked)}
              />
              Require login for favorites
            </label>
          </div>

          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={settings.requireLoginForRadio}
                onChange={(e) => handleSettingChange('requireLoginForRadio', e.target.checked)}
              />
              Require login for radio features
            </label>
          </div>

          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={settings.requireLoginForPlaylists}
                onChange={(e) => handleSettingChange('requireLoginForPlaylists', e.target.checked)}
              />
              Require login for playlists
            </label>
          </div>

          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={settings.allowGuestPlayback}
                onChange={(e) => handleSettingChange('allowGuestPlayback', e.target.checked)}
              />
              Allow guest playback
            </label>
          </div>
        </div>

        <div className="settings-group">
          <h3>Platform Features</h3>
          
          <div className="setting-item">
            <label>
              Max songs per playlist:
              <input
                type="number"
                value={settings.maxSongsPerPlaylist}
                onChange={(e) => handleSettingChange('maxSongsPerPlaylist', parseInt(e.target.value))}
                min="10"
                max="1000"
              />
            </label>
          </div>

          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={settings.enableSocialFeatures}
                onChange={(e) => handleSettingChange('enableSocialFeatures', e.target.checked)}
              />
              Enable social features
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

const UploadSongForm = ({ vibes, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    artist: 'MetaSoul®',
    album: '',
    genre: '',
    vibes: [],
    tags: [],
    audioFile: null,
    albumArt: null
  });

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [genres, setGenres] = useState([]);
  const [predefinedTags, setPredefinedTags] = useState([]);

  // Fetch genres and tags on mount
  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/genres`);
        if (response.ok) {
          const data = await response.json();
          setGenres(data);
        }
      } catch (error) {
        console.error('Failed to fetch genres:', error);
      }
    };
    
    const fetchTags = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tags`);
        if (response.ok) {
          const data = await response.json();
          setPredefinedTags(data.map(tag => tag.name));
        }
      } catch (error) {
        console.error('Failed to fetch tags:', error);
      }
    };
    
    fetchGenres();
    fetchTags();
  }, []);

  const handleTagToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) 
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.audioFile) {
      setUploadError('Please select an audio file');
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('artist', formData.artist);
      submitData.append('album', formData.album);
      submitData.append('genre', formData.genre);
      submitData.append('vibes', JSON.stringify(formData.vibes));
      submitData.append('tags', JSON.stringify(formData.tags));
      submitData.append('audio_file', formData.audioFile);
      
      if (formData.albumArt) {
        submitData.append('album_art', formData.albumArt);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/songs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: submitData
      });

      if (response.ok) {
        onClose();
      } else {
        const error = await response.json();
        setUploadError(error.detail || 'Failed to upload song');
      }
    } catch (error) {
      setUploadError('Network error. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="upload-form-overlay">
      <div className="upload-form">
        <div className="upload-form-header">
          <h3>Upload New Song</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {uploadError && (
          <div className="upload-error">
            {uploadError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Song Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Artist</label>
              <input
                type="text"
                value={formData.artist}
                onChange={(e) => setFormData(prev => ({ ...prev, artist: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Album</label>
              <input
                type="text"
                value={formData.album}
                onChange={(e) => setFormData(prev => ({ ...prev, album: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Genre</label>
              <select
                value={formData.genre}
                onChange={(e) => setFormData(prev => ({ ...prev, genre: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  background: '#fff',
                  color: '#000'
                }}
              >
                <option value="" style={{ background: '#fff', color: '#000' }}>Select Genre</option>
                {genres.map(genre => (
                  <option key={genre.id} value={genre.slug} style={{ background: '#fff', color: '#000' }}>
                    {genre.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Vibes</label>
            <div className="vibes-checkboxes">
              {vibes.map(vibe => (
                <label key={vibe.id} className="vibe-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.vibes.includes(vibe.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({
                          ...prev,
                          vibes: [...prev.vibes, vibe.id]
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          vibes: prev.vibes.filter(v => v !== vibe.id)
                        }));
                      }
                    }}
                  />
                  {vibe.name}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>
              Tags for Recommendations
              <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>
                (Select tags to improve "Made For You" recommendations)
              </span>
            </label>
            <div className="tags-selection" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: '8px',
              marginTop: '8px'
            }}>
              {predefinedTags.map(tag => {
                const isChecked = formData.tags.includes(tag);
                return (
                  <label 
                    key={tag} 
                    className="tag-checkbox"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      border: `2px solid ${isChecked ? '#9D4EDD' : '#ddd'}`,
                      borderRadius: '20px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      background: isChecked ? '#f3e8ff' : 'white',
                      transition: 'all 0.2s',
                      color: '#333'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleTagToggle(tag)}
                      style={{ marginRight: '6px' }}
                    />
                    <span style={{ color: '#333' }}>{tag}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Audio File *</label>
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => setFormData(prev => ({ ...prev, audioFile: e.target.files[0] }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Album Art</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFormData(prev => ({ ...prev, albumArt: e.target.files[0] }))}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="admin-button secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="admin-button primary" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload Song'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateVibeForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#9D4EDD'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/vibes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify(formData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create vibe');
      }

      console.log('✅ Vibe created successfully');
      
      // Invalidate cache to show new vibe immediately
      apiCache.invalidate('vibes');
      apiCache.invalidate('songs');
      
      onSuccess(); // Call the success callback
      onClose();
    } catch (err) {
      console.error('❌ Error creating vibe:', err);
      let errorMessage = err.message || 'Failed to create vibe';
      
      // Add more specific error messages
      if (errorMessage.includes('401') || errorMessage.includes('Authentication required')) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (errorMessage.includes('already exists')) {
        errorMessage = 'A vibe with this name already exists.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="upload-form-overlay">
      <div className="upload-form small">
        <div className="upload-form-header">
          <h3>Create New Vibe</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Vibe Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              placeholder="e.g. Divine Confidence"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the energy and feeling of this vibe..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Color</label>
            <input
              type="color"
              value={formData.color}
              onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
            />
          </div>

          {error && (
            <div className="error-message" style={{ color: 'red', marginBottom: '10px' }}>
              {error}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="admin-button secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="admin-button primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Vibe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditVibeModal = ({ vibe, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: vibe.name || '',
    description: vibe.description || '',
    color: vibe.color || '#9D4EDD',
    image_url: vibe.image_url || ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(vibe.image_url || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      // Upload image first if there's a new one
      if (imageFile) {
        console.log('📤 Uploading image to Bunny storage...', {
          vibeId: vibe.id,
          fileName: imageFile.name,
          fileSize: imageFile.size
        });
        
        const imageFormData = new FormData();
        imageFormData.append('file', imageFile);

        const imageResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/vibes/${vibe.id}/image`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
            body: imageFormData
          }
        );

        if (!imageResponse.ok) {
          const errorData = await imageResponse.json();
          console.error('❌ Image upload failed:', errorData);
          
          const errorMsg = errorData.detail || 'Failed to upload image';
          
          // Store error in localStorage so it persists through redirect
          localStorage.setItem('vibe_upload_error', JSON.stringify({
            error: errorMsg,
            timestamp: Date.now()
          }));
          
          // Show alert with error
          alert(`❌ Image upload failed!\n\n${errorMsg}\n\nModal will stay open so you can retry.`);
          
          throw new Error(errorMsg);
        }

        const imageData = await imageResponse.json();
        console.log('✅ Image uploaded successfully:', imageData);
        formData.image_url = imageData.image_url;
      }

      // Update vibe details
      console.log('📝 Updating vibe details...', formData);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/vibes/${vibe.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify(formData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Vibe update failed:', errorData);
        
        // Store error in localStorage
        localStorage.setItem('vibe_upload_error', JSON.stringify({
          error: errorData.detail || 'Failed to update vibe',
          timestamp: Date.now()
        }));
        
        throw new Error(errorData.detail || 'Failed to update vibe');
      }

      console.log('✅ Vibe updated successfully');
      
      // Clear any previous errors
      localStorage.removeItem('vibe_upload_error');
      
      setMessage('Vibe updated successfully!');
      
      // Add alert to see the actual image URL before redirect
      if (imageFile) {
        alert(`Image uploaded! URL: ${formData.image_url}\n\nCheck your Bunny storage "images" folder. Click OK to refresh.`);
      }
      
      setTimeout(() => {
        onSave();
      }, 1500);
    } catch (error) {
      console.error('❌ Error in handleSubmit:', error);
      setMessage(error.message || 'Failed to update vibe');
      // Don't call onSave if there was an error - stay on the modal
      setSaving(false);
      return; // Exit early, don't close modal
    } finally {
      if (message.includes('successfully')) {
        setSaving(false);
      }
    }
  };

  return (
    <div className="upload-form-overlay">
      <div className="upload-form small">
        <div className="upload-form-header">
          <h3>Edit Vibe: {vibe.name}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Vibe Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              placeholder="e.g. Divine Confidence"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the energy and feeling of this vibe..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Color</label>
            <input
              type="color"
              value={formData.color}
              onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label>Vibe Image <span style={{ color: '#9D4EDD', fontSize: '12px', fontWeight: 'normal' }}>(Recommended: 1200x630px)</span></label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
            {imagePreview && (
              <div style={{ marginTop: '10px' }}>
                <img 
                  src={imagePreview} 
                  alt="Vibe preview" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '300px', 
                    height: '200px', 
                    objectFit: 'cover', 
                    borderRadius: '8px' 
                  }} 
                />
              </div>
            )}
          </div>

          {message && (
            <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="admin-button secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="admin-button primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


const CreateGenreForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const handleNameChange = (name) => {
    setFormData(prev => ({
      ...prev,
      name,
      // Auto-generate slug from name
      slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/genres`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify(formData)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create genre');
      }

      setMessage('Genre created successfully!');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      setMessage(error.message || 'Failed to create genre');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="upload-form-overlay">
      <div className="upload-form small">
        <div className="upload-form-header">
          <h3>Create New Genre</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Genre Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              placeholder="e.g. Spiritual Meditation"
            />
          </div>

          <div className="form-group">
            <label>Slug (auto-generated) *</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
              required
              placeholder="spiritual-meditation"
            />
            <small style={{ color: '#b3b3b3', fontSize: '12px' }}>
              URL-friendly version (lowercase, hyphens)
            </small>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe this genre..."
              rows={3}
            />
          </div>

          {message && (
            <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="admin-button secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="admin-button primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create Genre'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditGenreModal = ({ genre, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: genre.name || '',
    slug: genre.slug || '',
    description: genre.description || ''
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/genres/${genre.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify(formData)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update genre');
      }

      setMessage('Genre updated successfully!');
      setTimeout(() => {
        onSave();
      }, 1500);
    } catch (error) {
      setMessage(error.message || 'Failed to update genre');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="upload-form-overlay">
      <div className="upload-form small">
        <div className="upload-form-header">
          <h3>Edit Genre: {genre.name}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Genre Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              placeholder="e.g. Spiritual Meditation"
            />
          </div>

          <div className="form-group">
            <label>Slug *</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
              required
              placeholder="spiritual-meditation"
            />
            <small style={{ color: '#b3b3b3', fontSize: '12px' }}>
              URL-friendly version (lowercase, hyphens)
            </small>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe this genre..."
              rows={3}
            />
          </div>

          {message && (
            <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="admin-button secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="admin-button primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdsManager = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    audioFile: null,
    imageFile: null,
    websiteUrl: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingImageAdId, setEditingImageAdId] = useState(null);
  const [updatingImage, setUpdatingImage] = useState(false);
  const [editingWebsiteAdId, setEditingWebsiteAdId] = useState(null);
  const [websiteEditValue, setWebsiteEditValue] = useState('');

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAds(data);
      } else {
        setError('Failed to fetch ads');
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
      setError('Error loading ads');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.title || !uploadForm.audioFile) {
      setError('Please provide title and audio file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('audio_file', uploadForm.audioFile);
      if (uploadForm.imageFile) {
        formData.append('image_file', uploadForm.imageFile);
      }
      if (uploadForm.websiteUrl) {
        formData.append('website_url', uploadForm.websiteUrl);
      }

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        setSuccess('Ad uploaded successfully!');
        setUploadForm({ title: '', audioFile: null, imageFile: null, websiteUrl: '' });
        // Reset file inputs
        document.getElementById('ad-audio-file').value = '';
        const imageInput = document.getElementById('ad-image-file');
        if (imageInput) imageInput.value = '';
        fetchAds();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to upload ad');
      }
    } catch (error) {
      console.error('Error uploading ad:', error);
      setError('Error uploading ad');
    } finally {
      setUploading(false);
    }
  };

  const handleWebsiteUpdate = async (adId) => {
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('website_url', websiteEditValue);

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${adId}/website`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        setSuccess('Website updated successfully!');
        setEditingWebsiteAdId(null);
        setWebsiteEditValue('');
        fetchAds();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to update website');
      }
    } catch (error) {
      console.error('Error updating website:', error);
      setError('Error updating website');
    }
  };

  const handleImageUpdate = async (adId, imageFile) => {
    if (!imageFile) return;

    setUpdatingImage(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('image_file', imageFile);

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${adId}/image`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        setSuccess('Ad image updated successfully!');
        setEditingImageAdId(null);
        fetchAds();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to update image');
      }
    } catch (error) {
      console.error('Error updating ad image:', error);
      setError('Error updating image');
    } finally {
      setUpdatingImage(false);
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${adId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('Ad deleted successfully');
        fetchAds();
      } else {
        setError('Failed to delete ad');
      }
    } catch (error) {
      console.error('Error deleting ad:', error);
      setError('Error deleting ad');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Advertisement Management</h2>
        <p>Upload and manage ads that play every 10 songs</p>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#ef4444',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px 16px',
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '8px',
          color: '#22c55e',
          marginBottom: '20px'
        }}>
          {success}
        </div>
      )}

      {/* Upload Form */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '30px'
      }}>
        <h3 style={{ marginBottom: '20px', color: 'white' }}>Upload New Ad</h3>
        <form onSubmit={handleUpload}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#999' }}>
              Ad Title *
            </label>
            <input
              type="text"
              value={uploadForm.title}
              onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
              placeholder="e.g., Summer Sale 2025"
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px'
              }}
              required
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#999' }}>
              Audio File * (MP3, WAV, etc.)
            </label>
            <input
              id="ad-audio-file"
              type="file"
              accept="audio/*"
              onChange={(e) => setUploadForm({ ...uploadForm, audioFile: e.target.files[0] })}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px'
              }}
              required
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#999' }}>
              Image File (Optional - shown as album art)
            </label>
            <input
              id="ad-image-file"
              type="file"
              accept="image/*"
              onChange={(e) => setUploadForm({ ...uploadForm, imageFile: e.target.files[0] })}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#999' }}>
              Website URL (Optional - clickable in expanded view)
            </label>
            <input
              type="url"
              value={uploadForm.websiteUrl}
              onChange={(e) => setUploadForm({ ...uploadForm, websiteUrl: e.target.value })}
              placeholder="https://example.com"
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px'
              }}
            />
          </div>

          <button
            type="submit"
            disabled={uploading}
            className="admin-button primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Upload size={18} />
            {uploading ? 'Uploading...' : 'Upload Ad'}
          </button>
        </form>
      </div>

      {/* Ads List */}
      <div>
        <h3 style={{ marginBottom: '16px', color: 'white' }}>
          Existing Ads ({ads.length})
        </h3>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            Loading ads...
          </div>
        ) : ads.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            color: '#999'
          }}>
            No ads uploaded yet. Upload your first ad above.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {ads.map((ad) => (
              <div
                key={ad.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                {/* Ad Image Preview */}
                <div style={{ position: 'relative' }}>
                  {ad.imageUrl ? (
                    <img
                      src={ad.imageUrl}
                      alt={ad.title}
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '8px',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '8px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#666',
                      fontSize: '12px'
                    }}>
                      No Image
                    </div>
                  )}
                </div>

                {/* Ad Info */}
                <div style={{ flex: 1 }}>
                  <h4 style={{ color: 'white', marginBottom: '8px', fontSize: '16px' }}>
                    {ad.title}
                  </h4>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#999', flexWrap: 'wrap' }}>
                    <span>📊 {ad.play_count} plays</span>
                    <span>📅 {formatDate(ad.created_at)}</span>
                    <span>⏱️ {ad.duration}s</span>
                    {ad.websiteUrl && <span>🔗 <a href={ad.websiteUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#9D4EDD' }}>{new URL(ad.websiteUrl).hostname}</a></span>}
                  </div>
                  
                  {/* Edit Website */}
                  {editingWebsiteAdId === ad.id ? (
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="url"
                        value={websiteEditValue}
                        onChange={(e) => setWebsiteEditValue(e.target.value)}
                        placeholder="https://example.com"
                        style={{
                          padding: '4px 8px',
                          background: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '4px',
                          color: 'white',
                          fontSize: '12px',
                          width: '250px'
                        }}
                      />
                      <button
                        onClick={() => handleWebsiteUpdate(ad.id)}
                        style={{
                          padding: '4px 8px',
                          background: 'rgba(157, 78, 221, 0.3)',
                          border: '1px solid rgba(157, 78, 221, 0.4)',
                          borderRadius: '4px',
                          color: '#9D4EDD',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingWebsiteAdId(null);
                          setWebsiteEditValue('');
                        }}
                        style={{
                          padding: '4px 8px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '4px',
                          color: 'white',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setEditingWebsiteAdId(ad.id);
                        setWebsiteEditValue(ad.websiteUrl || '');
                      }}
                      style={{
                        marginTop: '8px',
                        padding: '4px 8px',
                        background: 'rgba(157, 78, 221, 0.2)',
                        border: '1px solid rgba(157, 78, 221, 0.3)',
                        borderRadius: '4px',
                        color: '#9D4EDD',
                        fontSize: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginRight: '8px'
                      }}
                    >
                      🔗 {ad.websiteUrl ? 'Edit Website' : 'Add Website'}
                    </button>
                  )}

                  {/* Change Image Button/Input */}
                  {editingImageAdId === ad.id ? (
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleImageUpdate(ad.id, e.target.files[0]);
                          }
                        }}
                        disabled={updatingImage}
                        style={{
                          fontSize: '12px',
                          color: 'white',
                          maxWidth: '200px'
                        }}
                      />
                      <button
                        onClick={() => setEditingImageAdId(null)}
                        style={{
                          padding: '4px 8px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '4px',
                          color: 'white',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditingImageAdId(ad.id)}
                      style={{
                        marginTop: '8px',
                        padding: '4px 8px',
                        background: 'rgba(157, 78, 221, 0.2)',
                        border: '1px solid rgba(157, 78, 221, 0.3)',
                        borderRadius: '4px',
                        color: '#9D4EDD',
                        fontSize: '12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Upload size={12} />
                      {ad.imageUrl ? 'Change Image' : 'Add Image'}
                    </button>
                  )}
                </div>

                {/* Audio Preview */}
                <audio
                  controls
                  style={{ maxWidth: '250px' }}
                  src={ad.audioUrl}
                >
                  Your browser does not support audio playback.
                </audio>

                {/* Actions */}
                <button
                  onClick={() => handleDelete(ad.id)}
                  className="admin-button danger"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Analytics Summary */}
      {ads.length > 0 && (
        <div style={{
          marginTop: '30px',
          padding: '20px',
          background: 'linear-gradient(135deg, rgba(157, 78, 221, 0.1) 0%, rgba(199, 125, 255, 0.1) 100%)',
          borderRadius: '12px',
          border: '1px solid rgba(157, 78, 221, 0.2)'
        }}>
          <h4 style={{ color: 'white', marginBottom: '12px' }}>Analytics Summary</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#9D4EDD' }}>
                {ads.length}
              </div>
              <div style={{ fontSize: '13px', color: '#999' }}>Total Ads</div>
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#C77DFF' }}>
                {ads.reduce((sum, ad) => sum + ad.play_count, 0)}
              </div>
              <div style={{ fontSize: '13px', color: '#999' }}>Total Plays</div>
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#FFD700' }}>
                {Math.round(ads.reduce((sum, ad) => sum + ad.play_count, 0) / ads.length) || 0}
              </div>
              <div style={{ fontSize: '13px', color: '#999' }}>Avg Plays per Ad</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default AdminPanel;